import React from 'react';
import { Text, View, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import type { ApiContentBlockButton } from 'src/core/models/api/ApiContentBlock';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { pushPage } from 'src/presentation/helpers/NavigationHelper';

interface ContentBlockButtonProps {
	data: ApiContentBlockButton;
	first?: boolean;
	last?: boolean;
}

function ContentBlockButton({ data, first, last }: ContentBlockButtonProps) {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const eventStore = useEventStore();
	const { openUrlInternally } = useServicesStore();
	// Beheer can save a button without a label, so the text is not guaranteed.
	function textToUpperCase(text?: string) {
		return (text ?? '').toUpperCase();
	}

	function onButtonPress() {
		switch (data.data.cb_button_links_to) {
			case 'app': {
				const inAppLink = data.data.cb_button_app_link;
				const pages = inAppLink.includes('/') ? inAppLink.split('/') : [inAppLink];
				const inAppPage = eventStore.getPageByID(pages);
				if (!inAppPage) return;
				pushPage(navigation, inAppPage.type, inAppPage);
				break;
			}

			case 'website': {
				openUrlInternally({ url: data.data.cb_button_url_link }).catch(() => undefined);
				break;
			}
		}
	}

	return (
		<TouchableOpacity
			activeOpacity={0.6}
			style={{
				width: '100%',
				alignItems: 'center',
				justifyContent: 'center',
			}}
			onPress={() => onButtonPress()}
		>
			<View
				style={[
					styles.button,
					last ? styles.last : null,
					first ? styles.first : null,
					{ backgroundColor: eventStore.activeTheme.button_color },
				]}
			>
				<Text style={[styles.text, { color: eventStore.activeTheme.button_text_color }]}>
					{textToUpperCase(data.data.cb_button_text)}
				</Text>
			</View>
		</TouchableOpacity>
	);
}

export default ContentBlockButton;

const styles = StyleSheet.create({
	text: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
		color: Colors.white,
		paddingLeft: 22,
		paddingRight: 22,
		paddingTop: 17,
		paddingBottom: 17,
	},
	button: {
		backgroundColor: Colors.greyDark,
		marginTop: Dimens.margin20,
		marginBottom: Dimens.margin20,
		minWidth: 130,
		alignItems: 'center',
		justifyContent: 'center',
	},
	first: {
		marginTop: Dimens.margin0,
	},
	last: {},
});
